import React, { useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { get, post } from "../../services/Services";
import { BsFillSendFill, BsFillXCircleFill } from "react-icons/bs";
import "bootstrap/dist/css/bootstrap.min.css";
import SuccessModal from "./SuccessModal";
import * as Yup from "yup";

const PerformanceForm = ({ onSuccess, onclose, eventId, eventRegistrationId }) => {
  // ======== VALIDACIÓN ========
  const validationSchema = Yup.object().shape({
    nombre: Yup.string()
      .required("El nombre es obligatorio")
      .matches(/^[A-Z].*$/, "Debe comenzar con mayúscula."),
    categoria: Yup.string().required("La categoría es obligatoria"),
    modalidad: Yup.string().required("La modalidad es obligatoria"),
    formas: Yup.string().required("La forma es obligatoria"),
    duration: Yup.string().required("La duración es obligatoria"),
    musicTheme: Yup.string().required("El tema musical es obligatorio"),
  });

  // ======== ESTADOS ========
  const [eventoDetalles, setEventoDetalles] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    categoria: "",
    modalidad: "",
    formas: "",
    duration: "",
    musicTheme: "Default",
  });
  const [participants, setParticipants] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [isChoreography, setIsChoreography] = useState(false);
  const [hasatrainer, sethastrainer] = useState(false);
  //const [trainer, setTrainer] = useState(null);
  const [trainers, setTrainers] = useState([]);

  const [hasacoregrapher, sethascoreographer] = useState(false);
  const [coregrapher, setCoreographer] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [idformselected, setIdformselected] = useState(null);
  const [idcategoryselected, setIdcategoryselected] = useState(null);
  const [formDetails, setFormDetails] = useState(null);
  const [maxTime, setMaxTime] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);
  const [dancePerformanceOrder, setDancePerformanceOrder] = useState("");
  const [isRegistrationDisabled, setIsRegistrationDisabled] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const token = localStorage.getItem("accessToken");
  const decodedToken = jwtDecode(token);
  const schoolId = decodedToken.schools[0].id;

  // ======== FUNCIONES AUXILIARES ========
  const capitalizeName = (value) => {
    const words = value.split(" ");
    const exceptions = ["da", "de", "del", "la", "el", "y", "a", "en", "con", "&"];
    return words
      .map((word, index) =>
        exceptions.includes(word.toLowerCase()) && index > 0
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(" ");
  };

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationErrors) {
      const formErrors = {};
      validationErrors.inner.forEach((error) => {
        formErrors[error.path] = error.message;
      });
      setErrors(formErrors);
      return false;
    }
  };
  

  // ======== EFECTOS ========
  useEffect(() => {
    const fetchEventos = async () => {
      try {
        const response = await get(`/api/academy/events/${eventId}`);
        setEventoDetalles(response.data);
      } catch {
        console.error("Error al cargar eventos");
      }
    };
    fetchEventos();
  }, [eventId]);

  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        const response = await get(
          `/api/academy/schools/${schoolId}/members?page=${page}&size=2000&status=true&order_by=surname&orientation=asc`
        );
        setParticipants(response.data.content);
        setTotalPages(response.data.totalPages);
      } catch {
        console.error("Error al cargar los participantes");
      }
    };
    if (schoolId) fetchParticipants();
  }, [schoolId, page]);

  useEffect(() => {
    const fetchFormDetails = async () => {
      if (!idformselected) return;
      try {
        const response = await get(`/api/academy/forms/${idformselected}`);
        setFormDetails(response.data);
      } catch {
        console.error("Error al obtener detalles de la forma");
      }
    };
    fetchFormDetails();
  }, [idformselected]);

  useEffect(() => {
    const fetchValidTime = async () => {
      if (!idcategoryselected || !idformselected) return;
      try {
        const response = await get(
          `/api/academy/valid-time?categoryId=${idcategoryselected}&formId=${idformselected}`
        );
        setMaxTime(response.data.duration);
        setFormData((prev) => ({ ...prev, duration: response.data.duration }));
      } catch {
        console.error("Error al obtener la duración válida");
      }
    };
    fetchValidTime();
  }, [idcategoryselected, idformselected]);

  // Cálculo del precio total
  useEffect(() => {
    if (!eventoDetalles?.config) return;
    const pricePerParticipant =
      eventoDetalles.config.forms?.find((f) => f.id === parseInt(formData.formas))?.price || 0;
    const priceChoreography = eventoDetalles.config.priceChoreography || 0;
    const total =
      selectedParticipants.length * pricePerParticipant +
      (isChoreography ? priceChoreography : 0);
    setTotalPrice(total);
  }, [selectedParticipants, formData.formas, eventoDetalles, isChoreography]);

  // Deshabilitar botón si no cumple el rango de participantes
  useEffect(() => {
    if (!formDetails) return;
    const invalid =
      selectedParticipants.length > formDetails.maximumtparticipants ||
      selectedParticipants.length < formDetails.minimumParticipants;
    setIsRegistrationDisabled(invalid);
  }, [selectedParticipants, formDetails]);

  // ======== HANDLERS ========
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "formas") setIdformselected(value);
    if (name === "categoria") setIdcategoryselected(value);

    if (name === "nombre") {
      const formattedValue = capitalizeName(value);
      setFormData((prev) => ({ ...prev, [name]: formattedValue }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleParticipanteChange = (e) => {
    const { value, checked } = e.target;
    const participantId = value;
    if (formDetails && checked && selectedParticipants.length >= formDetails.maximumtparticipants) {
      alert(`No puedes seleccionar más de ${formDetails.maximumtparticipants} bailarines.`);
      return;
    }
    const participant = { member: { id: participantId }, type: { id: 1 } };
    setSelectedParticipants((prev) =>
      checked
        ? [...prev, participant].filter(
            (p, i, self) => self.findIndex((sp) => sp.member.id === p.member.id) === i
          )
        : prev.filter((p) => p.member.id !== participantId)
    );
  };

  const handleCoreografiaChange = (e) => {
    const checked = e.target.checked;
    setIsChoreography(checked);
    sethascoreographer(checked);
  };

  //const handlehasTrainerChange = (e) => {
    //sethastrainer(e.target.checked);
    //if (!e.target.checked) setTrainer(null);
  //};
  const handlehasTrainerChange = (e) => {
  sethastrainer(e.target.checked);
  if (!e.target.checked) setTrainers([]);
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) return;

    if (selectedParticipants.length < formDetails?.minimumParticipants) {
      setError(`Debes seleccionar al menos ${formDetails.minimumParticipants} participantes.`);
      return;
    }

    setLoading(true);
    setError(null);
    //const membersSelected = [...selectedParticipants, trainer, coregrapher].filter(Boolean);
    const membersSelected = [
  ...selectedParticipants,
  ...trainers,
  coregrapher,
].filter(Boolean);

    const payload = {
      name: formData.nombre,
      duration: formData.duration,
      musicTheme: formData.musicTheme,
      status: true,
      isChoreographed: isChoreography,
      contest: { id: 2 },
      category: { id: parseInt(formData.categoria) },
      modality: { id: parseInt(formData.modalidad) },
      totalPrice,
      order: parseInt(dancePerformanceOrder),
      form: { id: parseInt(formData.formas) },
      participants: membersSelected,
    };

    try {
      await post(`/api/academy/events/registration/${eventRegistrationId}/performance`, payload);
      setShowSuccessModal(true);
      onSuccess();
      onclose();
    } catch {
      setError("Error al registrar la obra. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleFormClose = () => {
    setFormData({
      nombre: "",
      categoria: "",
      modalidad: "",
      formas: "",
      duration: "",
      musicTheme: "Default",
    });
    setSelectedParticipants([]);
    setIsChoreography(false);
    onclose();
  };

  // ======== RENDER ========
  return (
    <div className="container mt-4">
      <h5 className="text-center mb-4 bg-primary text-white p-3 rounded">Nueva Obra</h5>
      <h5 className="text-center text-muted">Escuela N°: {eventRegistrationId}</h5>
      {error && <div className="alert alert-danger text-center">{error}</div>}

      <form onSubmit={handleSubmit} className="shadow-lg p-4 bg-light rounded">
        {/* Nombre */}
        <div className="form-floating mb-3">
          <input
            type="text"
            id="nombre"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            className={`form-control ${errors.nombre ? "is-invalid" : ""}`}
            placeholder="Nombre"
          />
          <label htmlFor="nombre">Nombre:</label>
          {errors.nombre && <div className="invalid-feedback">{errors.nombre}</div>}
        </div>

        {/* Modalidad */}
        <div className="form-floating mb-3">
          <select
            id="modalidad"
            name="modalidad"
            value={formData.modalidad}
            onChange={handleChange}
            className="form-control"
          >
            <option value="">Seleccionar Modalidad</option>
            {eventoDetalles?.config?.modalities
              ?.slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
          </select>
          <label htmlFor="modalidad">Modalidad</label>
        </div>

        {/* Forma */}
        <div className="form-floating mb-3">
          <select
            id="formas"
            name="formas"
            value={formData.formas}
            onChange={handleChange}
            className="form-control"
          >
            <option value="">Seleccionar Forma</option>
            {eventoDetalles?.config?.forms
              ?.slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
          </select>
          <label htmlFor="formas">Forma</label>
        </div>

        {idformselected && (
          <div className="mt-1">
            <strong>Precio por participante: $</strong>
            {eventoDetalles?.config?.forms?.find(
              (forma) => forma.id === parseInt(idformselected)
            )?.price || 0}
          </div>
        )}

        {/* Categoría */}
        <div className="form-floating mb-3 mt-3">
          <select
            id="categoria"
            name="categoria"
            value={formData.categoria}
            onChange={handleChange}
            className="form-control"
          >
            <option value="">Seleccionar Categoría</option>
            {eventoDetalles?.config?.categories
              ?.slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
          <label htmlFor="categoria">Categoría</label>
        </div>

        {maxTime && (
          <div className="mt-1">
            <strong className="text-danger">Duración máxima: </strong>
            {maxTime} minutos.
          </div>
        )}

        {/* Participantes */}
        <div className="form-group mb-3 mt-3">
          <label>Bailarines</label>
          <table className="table table-striped table-hover">
            <thead className="table-primary">
              <tr>
                <th className="text-center">Seleccionar</th>
                <th className="text-center">Nombre y Apellido</th>
              </tr>
            </thead>
            <tbody>
              {participants
                .slice()
                .sort((a, b) =>
                  `${a.surname} ${a.name}`.localeCompare(`${b.surname} ${b.name}`)
                )
                .map((p) => (
                  <tr key={p.id}>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        value={p.id}
                        onChange={handleParticipanteChange}
                        checked={selectedParticipants.some((sp) => sp.member.id === p.id.toString())}
                      />
                    </td>
                    <td>{`${p.surname || ""} ${p.name || ""}`}</td>
                  </tr>
                ))}
            </tbody>
          </table>

          {/* Paginación */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
            >
              ← Anterior
            </button>
            <span className="text-muted">
              Página {page + 1} de {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente →
            </button>
          </div>
        </div>

        {/* Preparador */}
        <div className="form-group mb-3">
          <label>
            <input type="checkbox" checked={hasatrainer} onChange={handlehasTrainerChange} /> Maestro/a Preparador/a
          </label>
          {hasatrainer && (
  <select
    multiple
    className="form-control mt-2"
    value={trainers.map((t) => t.member.id)}
    onChange={(e) => {
      const selectedOptions = Array.from(e.target.selectedOptions).map(
        (opt) => ({ member: { id: opt.value }, type: { id: 2 } })
      );
      setTrainers(selectedOptions);
    }}
  >
    {participants.map((p) => (
      <option key={p.id} value={p.id}>
        {p.name} {p.surname}
      </option>
    ))}
  </select>
)}
<small className="text-muted">Podés seleccionar más de un preparador manteniendo presionada la tecla Ctrl (Windows) o Cmd (Mac).</small>

        </div>

        {/* Coreografía */}
        <div className="form-group mb-3">
          <label>
            <input type="checkbox" checked={isChoreography} onChange={handleCoreografiaChange} /> Concursa por coreografía
          </label>
          {hasacoregrapher && (
            <select
              className="form-control mt-2"
              value={coregrapher?.member?.id || ""}
              onChange={(e) =>
                setCoreographer({ member: { id: e.target.value }, type: { id: 3 } })
              }
            >
              <option value="">Seleccione Coreógrafo/a</option>
              {participants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.surname}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Orden */}
        <div className="form-floating mb-3">
          <select
            id="dance_performance_order"
            name="dance_performance_order"
            value={dancePerformanceOrder}
            onChange={handleChange}
            className="form-control"
          >
            <option value="">Seleccionar orden</option>
            {[...Array(100)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
          <label htmlFor="dance_performance_order">Orden de Presentación</label>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <button
            type="submit"
            className="btn btn-success"
            disabled={loading || isRegistrationDisabled}
          >
            <BsFillSendFill className="me-2" />
            {loading ? "Registrando..." : "Registrar Obra"}
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleFormClose}>
            <BsFillXCircleFill className="me-2" />
            Cancelar
          </button>
        </div>
      </form>

      {showSuccessModal && (
        <SuccessModal
          show={showSuccessModal}
          handleClose={() => setShowSuccessModal(false)}
          message="Obra registrada correctamente 🎉"
        />
      )}
    </div>
  );
};

export default PerformanceForm;
